import React, { useEffect, useState } from 'react';
import { BoxesIcon, CloudIcon, CpuIcon, ShieldCheckIcon, TrendingUpIcon, UsersIcon } from 'lucide-react';
type Allocation = {
  id: string;
  label: string;
  amount: string;
  percent: number;
  Icon: React.ElementType;
  detail: string;
  connects: string;
  color: string;
};
const allocations: Allocation[] = [{
  id: 'inventory',
  label: 'Inventory & working capital',
  amount: '$2.95M',
  percent: 29.5,
  Icon: BoxesIcon,
  detail: 'Inventory and working capital required to fulfill the 2027 deployment plan.',
  connects: 'Funds the consumables annuity',
  color: '#60a5fa'
}, {
  id: 'talent',
  label: 'Talent acquisition & retention',
  amount: '$1.5M',
  percent: 15,
  Icon: UsersIcon,
  detail: 'Hiring and retention across field service, deployment, and enterprise account coverage.',
  connects: 'Scales the deployment engine',
  color: '#34d399'
}, {
  id: 'balance-sheet',
  label: 'Financial strength',
  amount: '$0.85M',
  percent: 8.5,
  Icon: ShieldCheckIcon,
  detail: 'Repayment of short-term loans to strengthen the balance sheet.',
  connects: 'Repays short-term loans',
  color: '#fbbf24'
}, {
  id: 'revenue',
  label: 'Revenue growth',
  amount: '$1.6M',
  percent: 16,
  Icon: TrendingUpIcon,
  detail: 'Advertising, trade shows, marketing programs, and performance bonuses.',
  connects: 'Accelerates channel demand',
  color: '#f472b6'
}, {
  id: 'operations',
  label: 'Operations & infrastructure',
  amount: '$1.1M',
  percent: 11,
  Icon: CpuIcon,
  detail: 'ERP, logistics capacity, and service vans.',
  connects: 'Builds scalable operations',
  color: '#a78bfa'
}, {
  id: 'product',
  label: 'Product & software development and launch',
  amount: '$2.0M',
  percent: 20,
  Icon: CloudIcon,
  detail: 'SIP, new flavors, hybrid unit, and home unit development and launch.',
  connects: 'Expands the product platform',
  color: '#22d3ee'
}];
const outcomes: Array<[string, string]> = [['1,500', 'JuLi™ units deployed in 2027'], ['$27.8M', '2027 revenue, 72% consumables'], ['$4.6M', '2027 EBITDA — cash-flow positive']];
export function UseOfFundsVisual() {
  const [active, setActive] = useState<string | null>(null);
  useEffect(() => {
    const clear = () => setActive(null);
    window.addEventListener('beforeprint', clear);
    return () => window.removeEventListener('beforeprint', clear);
  }, []);
  return <section className="grid min-h-0 min-w-0 flex-1 gap-3 lg:grid-cols-[.68fr_1.32fr] lg:gap-5" aria-label="Use of the $10M growth round" data-id="element-1885" data-mp="ffo7yrf920i4">
      <div className="flex min-h-0 min-w-0 flex-col gap-2.5" data-id="element-1886" data-mp="jichz2fp9dgo">
        <div className="relative flex flex-col justify-center overflow-hidden rounded-2xl border-2 border-blue-400 bg-blue-500 p-4 text-white" data-id="element-1887" data-mp="ogdypgpnl0oz">
          <p className="text-[10px] font-bold uppercase tracking-[.16em] text-blue-50" data-id="element-1888" data-mp="1uxrds2w69za7">Series C — growth round</p>
          <p className="mt-1 text-4xl font-bold leading-none tabular-nums" data-id="element-1889" data-mp="29yeysmu84nuw">$10.0M</p>
          <p className="mt-2 text-sm font-bold leading-snug" data-id="element-1890" data-mp="2a09cph72vfph">Every dollar of the $10M is deployed against the 2027 plan.</p>
        </div>

        <div className="flex flex-1 flex-col justify-center rounded-2xl border border-white/15 bg-white/5 p-3.5" data-id="element-1891" data-mp="207tucirvcp42">
          <p className="text-[10px] font-bold uppercase tracking-[.16em] text-blue-300" data-id="element-1892" data-mp="1w5yqp7mgtzn4">What this capital buys</p>
          <dl className="mt-2.5 space-y-2" data-id="element-1893" data-mp="1r9i6upmau7fu">
            {outcomes.map(([value, label]) => <div key={label} className="flex items-baseline gap-2.5 border-b border-white/10 pb-2 last:border-0 last:pb-0" data-id="element-1894" data-mp="1m82h4k96fcr4">
                <dt className="shrink-0 text-xl font-bold tabular-nums leading-none text-white" data-id="element-1895" data-mp="1hbu0f2fbzbhu">{value}</dt>
                <dd className="text-[11px] font-semibold leading-snug text-slate-300" data-id="element-1896" data-mp="1daiilj2p7uk7">{label}</dd>
              </div>)}
          </dl>
        </div>

        <div className="rounded-2xl border border-blue-300/35 bg-blue-400/10 p-3.5" data-id="element-1897" data-mp="37ni2vpi0xcxt">
          <p className="text-[10px] font-bold uppercase tracking-[.16em] text-blue-200" data-id="element-1898" data-mp="3eqeuiogbdhzf">Why this round is the last dilutive one</p>
          <p className="mt-1.5 text-[11px] leading-relaxed text-slate-300" data-id="element-1899" data-mp="3jnorqq9y2xxt">
            The plan reaches positive EBITDA in 2027. This capital funds the working-capital gap between committed orders and the consumables revenue they generate.
          </p>
        </div>
      </div>

      <div className="flex min-h-0 min-w-0 flex-col rounded-2xl border border-white/15 bg-white/5 p-3.5 sm:p-4" data-id="element-1900" data-mp="2vy3ynek2ukat">
        <div className="flex flex-wrap items-baseline justify-between gap-2" data-id="element-1901" data-mp="30qx1fwvzjik9">
          <p className="text-sm font-bold text-white" data-id="element-1902" data-mp="2m20vc5dhofma">Allocation of proceeds</p>
          <p className="text-[10px] font-semibold text-blue-200" data-id="element-1903" data-mp="2qz12ykpzlqfi">Select a line to see what it unlocks</p>
        </div>

        <div className="mt-2.5 flex h-3 overflow-hidden rounded-full bg-navy-950/60" aria-hidden="true" data-id="element-1904" data-mp="2d1chpbs0ch9x">
          {allocations.map((item, index) => <span key={item.id} className={`pd3-uof-segment h-full transition-opacity duration-300 ${active && active !== item.id ? 'opacity-25' : 'opacity-100'}`} style={{
          width: `${item.percent}%`,
          backgroundColor: item.color,
          animationDelay: `${index * 90}ms`
        }} data-id="element-1905" data-mp="1ydb41m6rw8lm" data-mpi={index} />)}
        </div>

        <ul className="mt-3 flex min-h-0 flex-1 flex-col justify-between gap-1.5" data-id="element-1906" data-mp="23bme9y0zcl4d">
          {allocations.map((item, index) => {
          const isActive = active === item.id;
          const dimmed = active !== null && !isActive;
          return <li key={item.id} data-id="element-1907" data-mp="2r1qedveym2qm" data-mpi={index}>
                <button type="button" onClick={() => setActive(isActive ? null : item.id)} aria-pressed={isActive} className={`grid w-full grid-cols-[auto_1fr_auto] items-center gap-2.5 rounded-xl border px-2.5 py-2 text-left transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-300 ${isActive ? 'bg-white/10' : 'border-white/10 bg-navy-950/40 hover:border-white/25'} ${dimmed ? 'opacity-40' : 'opacity-100'}`} style={isActive ? {
              borderColor: item.color
            } : undefined} data-id="element-1908" data-mp="2r0or8sko5psj">
                  <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${isActive ? 'text-navy-950' : 'bg-white/10'}`} style={isActive ? {
                backgroundColor: item.color
              } : {
                color: item.color
              }} data-id="element-1909" data-mp="21pkdp8osv4e9">
                    <item.Icon className="h-4 w-4" aria-hidden="true" data-id="element-1910" data-mp="2gegi8vrndza4" />
                  </span>

                  <span className="min-w-0" data-id="element-1911" data-mp="2lg5weka62t2f">
                    <span className="flex items-baseline justify-between gap-2" data-id="element-1912" data-mp="2ld08h8d9xx8a">
                      <span className="truncate text-xs font-bold text-white" data-id="element-1913" data-mp="2pebznibsqqil">{item.label}</span>
                      <span className="shrink-0 text-[10px] font-bold tabular-nums" style={{
                    color: item.color
                  }} data-id="element-1914" data-mp="r2muky93sobb">{item.percent}%</span>
                    </span>
                    <span className="mt-1 block h-1.5 overflow-hidden rounded-full bg-white/10" data-id="element-1915" data-mp="njxq4urewxiy">
                      <span className="pd3-val-bar block h-full rounded-full" style={{
                    ['--pd3-val-w' as string]: `${item.percent / 29.5 * 100}%`,
                    backgroundColor: item.color,
                    animationDelay: `${index * 110}ms`
                  }} data-id="element-1916" data-mp="ir4ls7zuvr7e" />
                    </span>
                    <span className="mt-1 block truncate text-[10px] leading-snug text-slate-400" data-id="element-1917" data-mp="16ggu75nczv8n">
                      {isActive ? item.detail : item.connects}
                    </span>
                  </span>

                  <span className="shrink-0 text-sm font-bold tabular-nums text-white" data-id="element-1918" data-mp="xf8ucj572mcg">{item.amount}</span>
                </button>
              </li>;
        })}
        </ul>

        <p className="mt-2.5 border-t border-white/15 pt-2 text-[9px] leading-relaxed text-slate-400" data-id="element-1919" data-mp="3i82jmb9yi049">
          Source: Company management. Allocation reflects the planned deployment of the $10.0M Series C round and is subject to change based on commercial timing.
        </p>
      </div>
    </section>;
}